import { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import type { z } from 'zod';
import { Filter } from 'lucide-react';
import {
  Button,
  FieldSeparator,
  InfoHint,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  UserCard,
  toUserCardProps
} from '@bliks-io/ui';
import {
  FormInput,
  FormTextarea,
  FormUserSelect,
  FormDepartmentSelect,
  FormUserTransferListbox
} from '@/components/form/rhf';
import { DepartmentSelect } from '@/components/form/DepartmentSelect';
import {
  AddOrgFunctionRequestSchema,
  type AddOrgFunctionRequest,
  type UpdateOrgFunctionRequest,
  type OrgFunction,
  type OrgFunctionKind
} from '@bliks-io/contracts';
import { FormDialog } from '@/components/dialogs/FormDialog';
import { extractEntityId } from '@/lib/form-utils';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { orgFunctionCopyPrefix } from '@/lib/orgFunctionRoutes';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { leadLabelKeys } from '../utils/leadLabelKeys';

type AddOrgFunctionFormInput = z.input<typeof AddOrgFunctionRequestSchema>;

/** Per-kind dialog chrome — the page blocks own the create/edit copy. */
function kindDialogChrome(kind: OrgFunctionKind) {
  return { copyPrefix: orgFunctionCopyPrefix(kind), icon: ENTITY_ICONS[kind] };
}

/**
 * Per-open-cycle membership seed (edit mode only):
 * - 'ready' — the members query settled successfully; the picker mounts with
 *   the current holders (secondary + locked mainRole) as seed.
 * - 'unavailable' — the query settled in error; the dialog mounts WITHOUT the
 *   members field so a save can never wipe existing membership.
 *
 * The holder partition travels with the decision: it is the same snapshot the
 * form defaults, the lock set and the submit strip all read, so they cannot
 * disagree.
 */
interface MembersSeed {
  decision: 'ready' | 'unavailable';
  /** Holders via `secondaryRoles` — the editable part of the seed. */
  secondaryHolderIds: string[];
  /** Holders via `mainRole` — locked rows, stripped again on submit. */
  mainRoleHolderIds: string[];
}

/** Create mode has no holders to seed and nothing to lock. */
const CREATE_MODE_SEED: MembersSeed = {
  decision: 'ready',
  secondaryHolderIds: [],
  mainRoleHolderIds: []
};

/**
 * One pass, disjoint buckets: a mainRole match wins, so a (historical)
 * dual-linked user seeds once, as a locked row.
 */
function partitionHolders(
  orgFunctionId: string,
  members: NonNullable<ReturnType<typeof useOrgMembers>['data']>
): Pick<MembersSeed, 'secondaryHolderIds' | 'mainRoleHolderIds'> {
  const secondaryHolderIds: string[] = [];
  const mainRoleHolderIds: string[] = [];
  for (const member of members) {
    if (member.mainRole?._id === orgFunctionId) mainRoleHolderIds.push(member._id);
    else if (member.secondaryRoles?.some((ref) => ref._id === orgFunctionId))
      secondaryHolderIds.push(member._id);
  }
  return { secondaryHolderIds, mainRoleHolderIds };
}

/**
 * Derives once per open cycle how the edit form's membership seed mounts.
 *
 * FormDialog snapshots defaultValues on the closed→open transition only, so
 * the membership seed is a render-ordering invariant: if the edit form ever
 * mounts with a too-early/stale [] seed, every existing holder counts as
 * deselected and a save silently strips their membership. The seed is
 * therefore frozen once taken — background refetches must neither unmount the
 * already-open dialog nor swap it mid-edit, which for the holder partition
 * would unlock a row or let a stale id through the submit strip — and it
 * resets when the dialog closes.
 *
 * Implemented as guarded render-phase updates (React's "adjust state during
 * render"). Waiting on isFetching (not just isLoading) covers background
 * refetches too — reopening during a post-save refetch must wait, or it would
 * snapshot the pre-save holder set from cache.
 */
function useMembersSeed(
  open: boolean,
  orgFunction: OrgFunction | undefined,
  membersQuery: ReturnType<typeof useOrgMembers>
): MembersSeed | null {
  const [seed, setSeed] = useState<MembersSeed | null>(null);

  if (!open) {
    if (seed !== null) setSeed(null);
  } else if (orgFunction && seed === null && !membersQuery.isFetching) {
    if (membersQuery.isSuccess) {
      setSeed({
        decision: 'ready',
        ...partitionHolders(orgFunction._id, membersQuery.data)
      });
    } else if (membersQuery.isError) {
      setSeed({ decision: 'unavailable', secondaryHolderIds: [], mainRoleHolderIds: [] });
    }
  }

  return seed;
}

/**
 * The locked-row clause is role-only: a team can never be someone's Stelle
 * (mainRole), so on a team dialog it would describe a state the data model
 * cannot produce.
 */
function MembersHint({ kind }: { kind: OrgFunctionKind }) {
  const { t } = useTranslation();
  const hint = t('organization.orgFunctions.members.hint');
  if (kind !== 'role') return <InfoHint content={hint} />;
  return (
    <InfoHint content={`${hint} ${t('organization.orgFunctions.members.hintMainRoleLocked')}`} />
  );
}

/**
 * Dual-column picker for the OrgFunction's holders. Users holding it as their
 * Stelle (mainRole) show up selected but locked — read-only, since that link is
 * changed on the person, not here. Only the `secondaryRoles` link is editable.
 * The lead is managed via its own field and is not a member.
 */
function MembersPickerField({
  form,
  seedIds,
  lockedIds
}: {
  form: UseFormReturn<AddOrgFunctionFormInput, object, AddOrgFunctionRequest>;
  seedIds: string[];
  lockedIds: string[];
}) {
  const { t } = useTranslation();
  // FormDialog snapshots defaultValues only when it opens, and an edit seed can
  // arrive later; resetting after the Controller below has registered makes
  // the seed the field's default, so an untouched picker stays out of the
  // dirty-subset PATCH.
  const [initialSeedIds] = useState(seedIds);
  const { resetField } = form;
  useLayoutEffect(
    function seedMembersDefault() {
      resetField('members', { defaultValue: initialSeedIds });
    },
    [resetField, initialSeedIds]
  );
  // Lives inside the dialog body, which unmounts on close — so the filter
  // resets per open cycle without extra wiring.
  const [filterDepartmentId, setFilterDepartmentId] = useState<string | undefined>(undefined);

  return (
    <FormUserTransferListbox
      control={form.control}
      name="members"
      // No status filter on purpose (the component defaults to a 3-status
      // filter): the picker must hit the same query as the membership seed in
      // the dialog so both share one react-query cache entry.
      queryOptions={{ classification: 'internal', status: undefined }}
      lockedIds={lockedIds}
      lockedHint={t('organization.orgFunctions.members.mainRoleLocked')}
      leftLabel={t('organization.orgFunctions.members.available')}
      rightLabel={t('organization.orgFunctions.members.selected')}
      searchPlaceholder={t('organization.orgFunctions.members.search')}
      availableFilterSlot={
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="icon"
              title={t('organization.orgFunctions.members.filterByDepartment')}
              aria-label={t('organization.orgFunctions.members.filterByDepartment')}
              className="relative"
            >
              <Filter className="h-4 w-4" />
              {filterDepartmentId && (
                <span
                  aria-hidden="true"
                  className="absolute right-1 top-1 h-2 w-2 rounded-full bg-primary"
                />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end">
            <DepartmentSelect
              value={filterDepartmentId}
              onChange={setFilterDepartmentId}
              placeholder={t('organization.orgFunctions.members.filterByDepartment')}
              allowClear
              clearLabel={t('departmentSelect.allDepartments')}
            />
          </PopoverContent>
        </Popover>
      }
      availableFilter={
        filterDepartmentId ? (item) => item.user.department?._id === filterDepartmentId : undefined
      }
      renderItem={(item) => (
        <UserCard user={toUserCardProps(item.user)} variant="with-role" className="p-0" />
      )}
    />
  );
}

function MembersSlot({
  seed,
  form,
  seedIds
}: {
  seed: MembersSeed | null;
  form: UseFormReturn<AddOrgFunctionFormInput, object, AddOrgFunctionRequest>;
  seedIds: string[];
}) {
  const { t } = useTranslation();
  if (seed === null) {
    return (
      <div role="status" aria-label={t('common.loading')} className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  if (seed.decision === 'unavailable') {
    return (
      <p className="text-sm text-muted-foreground">
        {t('organization.orgFunctions.members.loadError')}
      </p>
    );
  }
  return <MembersPickerField form={form} seedIds={seedIds} lockedIds={seed.mainRoleHolderIds} />;
}

interface OrgFunctionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AddOrgFunctionRequest | UpdateOrgFunctionRequest) => unknown;
  /**
   * The kind this dialog creates. Kind is fixed by the surface that opens the
   * dialog (teams page → team, functions page → role) and immutable after
   * creation — there is no kind control in the form. In edit mode the entity's
   * own kind wins.
   */
  kind: OrgFunctionKind;
  orgFunction?: OrgFunction;
  /** Seed the create form from another source (e.g. a suggestion being approved). */
  defaultValuesOverride?: Partial<AddOrgFunctionFormInput>;
  /** Override the dialog title — used when reusing this form to approve a suggestion. */
  title?: string;
  /** Override the dialog description — used when reusing this form to approve a suggestion. */
  description?: string;
  /** Override the submit button label — used when reusing this form to approve a suggestion. */
  submitLabel?: string;
}

/**
 * Form dialog for creating/editing OrgFunctions (the merged Team + Role entity,
 * presented per kind — see ADR 0004).
 *
 * Only `name` and `kind` are required; `department`, `lead` (Funktionsleiter),
 * `email`, and `description` are optional on both kinds. The `members` field
 * carries every holder — Stelle (mainRole) holders included, locked and
 * stripped again on submit — while the PATCH payload stays the users'
 * `secondaryRoles` link only.
 */
export function OrgFunctionFormDialog({
  open,
  onOpenChange,
  onSubmit,
  kind,
  orgFunction,
  defaultValuesOverride,
  title,
  description,
  submitLabel
}: OrgFunctionFormDialogProps) {
  const { t } = useTranslation();
  const isEditing = !!orgFunction;
  const effectiveKind = orgFunction?.kind ?? kind;
  const { copyPrefix, icon } = kindDialogChrome(effectiveKind);

  // Externals can never hold an OrgFunction (reclassification strips all
  // links), so the picker only offers internal people. The picker's
  // FormUserTransferListbox below issues the identical query (classification
  // only, no status filter), so both share one react-query cache entry.
  const membersQuery = useOrgMembers({ classification: 'internal' });

  // The users currently holding this OrgFunction — membership is derived
  // user-side, so the edit seed comes from the members' own mainRole /
  // secondaryRoles links rather than from the OrgFunction doc. Only the
  // secondary link is editable here; mainRole holders seed as locked rows.
  // Why the freeze exists: see useMembersSeed — an empty too-early members
  // seed would deselect every existing holder on save.
  const membersSeed = useMembersSeed(open, orgFunction, membersQuery);
  const seed = isEditing ? membersSeed : CREATE_MODE_SEED;

  // Until the seed is 'ready' (still pending, or 'unavailable') the members
  // field never mounts: no Controller registers, `members` stays out of
  // defaultValues and out of the dirty-subset PATCH, so a save cannot wipe
  // membership.
  const membersFieldAvailable = seed?.decision === 'ready';
  const mainRoleHolderIds = seed?.mainRoleHolderIds ?? [];
  const membersDefault = defaultValuesOverride?.members ?? [
    ...(seed?.secondaryHolderIds ?? []),
    ...mainRoleHolderIds
  ];

  // lead/department stay undefined when unset: the contract rejects '' for id
  // fields (min(1)), and the selects emit undefined on clear anyway.
  const defaultValues: AddOrgFunctionFormInput = {
    name: defaultValuesOverride?.name ?? orgFunction?.name ?? '',
    kind: effectiveKind,
    email: defaultValuesOverride?.email ?? orgFunction?.email ?? '',
    description: defaultValuesOverride?.description ?? orgFunction?.description ?? '',
    lead: defaultValuesOverride?.lead ?? extractEntityId(orgFunction?.lead),
    department: defaultValuesOverride?.department ?? extractEntityId(orgFunction?.department),
    ...(membersFieldAvailable ? { members: membersDefault } : {})
  };

  // On edit, FormDialog delivers only the dirty field subset. A cleared
  // optional field arrives as a present key with value undefined (the schema
  // transform strips '') and must become null so the server $unsets it —
  // untouched fields stay absent so their stored values survive. `kind` is
  // immutable (the PATCH contract dropped it) and is never part of the edit
  // form, so it can't appear in the dirty subset.
  const handleSubmit = (data: AddOrgFunctionRequest) => {
    if (!isEditing) {
      return onSubmit(data);
    }
    const update: UpdateOrgFunctionRequest = {};
    if ('name' in data) update.name = data.name;
    if ('description' in data) update.description = data.description;
    if ('email' in data) update.email = data.email ?? null;
    if ('lead' in data) update.lead = data.lead ?? null;
    if ('department' in data) update.department = data.department ?? null;
    // The availability guard backs up the field-never-mounted invariant: when
    // the seed was unavailable, a members key must not reach the PATCH (absent
    // = leave membership untouched server-side). Locked mainRole holders are
    // stripped so the payload keeps its contract meaning: the full desired set
    // of SECONDARY holders.
    if (membersFieldAvailable && 'members' in data) {
      const mainRoleLockedSet = new Set(mainRoleHolderIds);
      update.members = data.members?.filter((id) => !mainRoleLockedSet.has(id));
    }
    return onSubmit(update);
  };

  return (
    <FormDialog<AddOrgFunctionFormInput, AddOrgFunctionRequest>
      open={open}
      onOpenChange={onOpenChange}
      title={title ?? (isEditing ? t(`${copyPrefix}.edit`) : t(`${copyPrefix}.create`))}
      description={
        description ??
        (isEditing ? t(`${copyPrefix}.editDescription`) : t(`${copyPrefix}.createDescription`))
      }
      icon={icon}
      schema={AddOrgFunctionRequestSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isEditing={isEditing}
      submitLabel={submitLabel}
    >
      {({ form }) => {
        const leadKeys = leadLabelKeys(effectiveKind);
        return (
          <div className="flex flex-col gap-8">
            {/* Basic Information Section — kind is fixed by the opening
                surface and never editable, so name is the only basic field. */}
            <FormInput
              control={form.control}
              name="name"
              label={t('common.Name')}
              placeholder={t('organization.orgFunctions.namePlaceholder')}
              required
            />

            <div className="flex flex-col gap-4">
              <FieldSeparator>{t('organization.departments.relationships')}</FieldSeparator>

              {/* Relationships Section — both optional */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormDepartmentSelect
                  control={form.control}
                  name="department"
                  label={t('organization.orgFunctions.department')}
                  placeholder={t('organization.orgFunctions.selectDepartment')}
                  allowClear
                  clearLabel={t('organization.orgFunctions.noDepartment')}
                />

                <FormUserSelect
                  control={form.control}
                  name="lead"
                  label={t(leadKeys.label)}
                  placeholder={t(leadKeys.placeholder)}
                  allowClear
                  clearLabel={t(leadKeys.clear)}
                />
              </div>

              <FormInput
                control={form.control}
                name="email"
                label={t('organization.orgFunctions.email')}
                placeholder={t('organization.orgFunctions.emailPlaceholder')}
                type="email"
              />

              {/* Members — the users holding this OrgFunction. Stelle
                (mainRole) holders show read-only/locked; only the
                `secondaryRoles` link is editable here. The lead is managed via
                its own field and is not a member. */}
              <div className="grid grid-cols-1 gap-2">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="members">{t('organization.orgFunctions.members.label')}</Label>
                  <MembersHint kind={effectiveKind} />
                </div>
                <MembersSlot seed={seed} form={form} seedIds={membersDefault} />
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <FieldSeparator>{t('common.Details')}</FieldSeparator>

              <FormTextarea
                control={form.control}
                name="description"
                label={t('common.Description')}
                placeholder={t('organization.orgFunctions.descriptionPlaceholder')}
                rows={4}
              />
            </div>
          </div>
        );
      }}
    </FormDialog>
  );
}
